/**
 * Ce qu'il faut pour qu'un accord compte, et quand l'écran passe au suivant.
 *
 * Pur : ni React, ni transport, ni horloge. Le hook qui l'alimente est
 * `use-chord-take` ; la règle, elle, est ici, où elle se teste. C'est le même
 * partage que `rhythm.ts` et `use-rhythm-take`.
 *
 * ## C'est le joueur qui déclenche la musique, pas l'inverse
 *
 * L'écran faisait sonner le passage d'origine dès l'arrivée sur l'accord, puis
 * attendait la prise par-dessus. Qui apprend cherche ses touches, donc pose
 * l'accord en retard, donc l'entend contre la musique : le seul moment où
 * l'écran joue est le moment où le joueur ne joue pas encore juste.
 *
 * L'ordre est inversé. **Rien ne sonne tant que l'accord n'est pas plaqué en
 * entier.** L'écran montre l'accord et attend, aussi longtemps qu'il le faut,
 * sans compter, sans presser. Le passage part à l'instant où les touches sont
 * toutes enfoncées, et le joueur se retrouve donc en place *avant* le premier
 * son : il ne peut plus être en retard sur une musique qu'il déclenche
 * lui-même.
 *
 * Ce qui remplace la tenue demandée, c'est la musique. On garde l'accord le
 * temps du passage parce qu'on l'entend courir, et non parce qu'un seuil a été
 * fixé quelque part.
 *
 * ## Le passage va jusqu'au bout, quoi qu'il arrive ensuite
 *
 * Lâcher après avoir plaqué ne coupe rien et n'annule rien. La règle a la
 * précision de ce qu'elle mesure : ce qui compte est **d'avoir posé l'accord
 * juste**, une fois, au bon endroit. Ce qui vient après appartient au joueur,
 * qui peut tenir, relâcher, ou préparer déjà le suivant.
 *
 * C'est aussi ce qui rend la règle enseignable en deux accords : on plaque, la
 * musique part, on lâche, elle continue. Rien à deviner.
 */

/**
 * Combien de temps la lueur met à retomber quand rien n'est plaqué.
 *
 * Court, parce que retomber est le contraire de progresser et qu'une chute
 * lente se lirait comme une seconde animation. Pas nul non plus : une coupure
 * franche dans une lumière d'ambiance fait un clignotement.
 */
export const TAKE_FALL_MS = 180;

export type TakeDeadline = {
  /** Quand l'accord a été plaqué en entier. */
  readonly struckAt: number;
  /** Combien de temps le morceau sonne sous cet accord. */
  readonly holdMs: number;
};

/**
 * L'instant du passage, dans la même horloge que les entrées.
 *
 * La fin du passage, et rien d'autre : il part à la frappe et dure ce que dure
 * la fenêtre du morceau. Une date et non un délai, parce que l'appelant
 * recalcule à chaque fois que son rappel change d'identité, et qu'un délai
 * relatif repartirait de zéro à chaque recalcul.
 *
 * Une fenêtre de durée nulle, qui n'arrive qu'entre deux rendus après un saut,
 * passe à l'instant même plutôt que de reculer.
 */
export function takeDueAt({ struckAt, holdMs }: TakeDeadline): number {
  return struckAt + Math.max(holdMs, 0);
}

/**
 * Combien de temps la musique continue après une porte, avant de s'arrêter.
 *
 * ## Le retard existe, désormais
 *
 * Frapper en avance a toujours été gratuit : la frappe est retenue, la porte
 * s'ouvre d'elle-même à la fin du segment et le passage enchaîne. Frapper en
 * retard coûtait du silence, immédiatement, sans seuil. Un joueur centré sur
 * le temps est donc en retard une fois sur deux par construction, et il
 * entendait un trou une porte sur deux. Rien de musical ne justifiait cette
 * dissymétrie ; elle tenait à ce que l'avance se code sans horloge et que le
 * retard demande un délai.
 *
 * Le délai est celui-ci. Le segment continue de sonner **au-delà** de la
 * porte, comme si elle avait été franchie ; si la frappe arrive pendant ce
 * temps, elle l'a été, et rien ne s'est passé. Sinon la musique s'arrête et
 * la tête de lecture revient sur la porte, d'où la frappe tardive repartira.
 * C'est une lecture spéculative : jouée pour de bon si la frappe vient,
 * annulée sinon.
 *
 * C'est la première fois qu'un retard existe dans cet écran, et c'est voulu.
 * Il ne coûte rien à qui cherche ses touches — le silence l'attendait déjà,
 * il l'attend toujours — et il rend la musique à qui la tient à peu près.
 *
 * ## Proportionnelle, et plafonnée
 *
 * Un délai fixe avale les portes rapprochées : à six dixièmes du tempo écrit,
 * une double croche à 120 vaut 208 ms d'écoute, et 300 ms de grâce
 * dépasseraient la porte suivante. La grâce est donc une part du segment
 * dans lequel elle déborde, et jamais plus que ce plafond, qui est l'ordre de
 * grandeur d'un retard qu'on n'entend pas comme une faute.
 *
 * C'est bien le segment **d'après** qui la mesure, celui que la porte ouvre,
 * et non celui qui vient de finir : c'est dans lui que la tête déborde, et
 * c'est sa porte de sortie qu'il ne faut pas atteindre.
 */
export const GRACE_MAX_MS = 300;

/** La part d'un segment que la grâce peut prendre. */
export const GRACE_SHARE = 0.3;

export function graceMs(segmentMs: number): number {
  return Math.min(GRACE_MAX_MS, Math.max(segmentMs, 0) * GRACE_SHARE);
}

/**
 * L'état d'une prise, tel que l'écran a besoin de le connaître.
 *
 * Deux questions et non trois : « l'accord a-t-il été plaqué » décide de tout,
 * puisque c'est lui qui lance le passage, et « les doigts sont-ils encore
 * dessus » n'intéresse plus personne une fois qu'il l'a été.
 */
export type ChordTake = {
  /** L'accord compte comme pris. */
  readonly taken: boolean;
  /** Il a été plaqué en entier : le passage court. */
  readonly struck: boolean;
};

/**
 * Ce que la lueur du fond montre de la prise.
 *
 * Elle monte. C'est le seul geste qu'elle fait, et il dure exactement le
 * passage : sombre à l'arrivée sur l'accord, elle se remplit de la frappe
 * jusqu'à la dernière note du morceau, puis retombe d'un coup sur l'accord
 * suivant, qui recommence à attendre.
 *
 * Ce qu'elle mesurait avant, la tenue demandée, n'existe plus. Ce qu'elle
 * mesure maintenant est plus long et plus musical : le temps qu'on passe sur
 * cet accord-ci. Elle n'a plus de terme à annoncer, donc plus d'or : le terme
 * est le changement d'accord lui-même, que l'écran montre déjà en changeant.
 *
 * Elle ne peut pas se confondre avec la barre du temps sous les notes, qui
 * parcourt la même durée dans l'autre sens : la barre est un objet net, petit,
 * gradué, et elle se **vide** ; la lueur est un fond diffus, sans bord, et elle
 * se **remplit**. Sens opposés, formes opposées, échelles opposées.
 *
 * Rien n'est jugé. Un accord pas encore plaqué n'est pas une faute, c'est un
 * écran qui attend : pas de couleur d'erreur, pas de compte, pas de message.
 */
export type TakeGlow = {
  /** L'opacité que vise la couche violette, entre 0 et 1. */
  readonly rise: number;
  /** En combien de temps elle l'atteint, en millisecondes. */
  readonly riseMs: number;
};

export function takeGlow({
  armed,
  struck,
  holdMs,
}: {
  /** Le décompte d'ouverture est passé. */
  readonly armed: boolean;
  /** L'accord a été plaqué : le passage court. */
  readonly struck: boolean;
  /** Ce que dure le passage, dont la montée est la mesure. */
  readonly holdMs: number;
}): TakeGlow {
  return armed && struck
    ? { rise: 1, riseMs: Math.max(holdMs, 0) }
    : { rise: 0, riseMs: TAKE_FALL_MS };
}
